/**
 * Dependencies
 */
import Vue from 'vue';
import axios from 'axios';
import { EventBus } from './event-bus';
/**
 * Components
 */
import SpeakerCard from './components/SpeakerCard.vue';
import CheckboxFilters from './components/CheckboxFilters';
import SpeakersCount from './components/SpeakersCount';
import PaginationNav from './components/pagination-nav';
import SpeakerSearch from './components/SpeakerSearch';
import SpeakerModal from './components/SpeakerModal.vue';

/**
 * Polyfill
 */
const queryString = require('query-string');
require('intersection-observer');

/**
 * Vue instance
 */
export default (() => {
    // Check for DOM element
    if (!document.querySelector('#speakers')) return false;

    return new Vue({
        el: '#speakers',

        components: {
            'speaker-card': SpeakerCard,
            'checkbox-filters': CheckboxFilters,
            'speakers-count': SpeakersCount,
            'pagination-nav': PaginationNav,
            'speaker-search': SpeakerSearch,
            'speaker-modal': SpeakerModal,
        },
        data: {
            loading: true,
            view: {
                filters: false,
                activeModelSearchTerm: null,
            },
            api: {
                speakersTitle: 'Speakers',
                speakersRequest: null,
                expertiseRequest: null,
                speakerTypeIDs: [],
                stateIDs: [],
                page: [],
                search: {},
            },
            data: {
                speakers: [],
                speakersMeta: {
                    pagination: {
                        totalPage: null,
                        currentPage: null,
                    },
                },
                expertise: null,
                states: null,
            },
        },
        computed: {
            speakersQueryParams: function () { // eslint-disable-line object-shorthand, func-names
                // Var to contain parameters
                let params = '';

                // Get search key
                const searchKey = Object.keys(this.api.search).map(key => key);

                // If searchKey has a value
                if (this.api.search[searchKey] === '') {

                    // Stringify params
                    params = queryString.stringify({
                        expertise: this.api.speakerTypeIDs,
                        states: this.api.stateIDs,
                        page: this.api.page,
                    }, { arrayFormat: 'comma' });

                } else {

                    // Stringify params, including searchKey
                    params = queryString.stringify({
                        expertise: this.api.speakerTypeIDs,
                        states: this.api.stateIDs,
                        page: this.api.page,
                        [searchKey]: this.api.search[searchKey],
                    }, { arrayFormat: 'comma' });
                }

                // If params is empty, return existing URL
                if (!params) return this.api.speakersRequest;

                // Determine divider based on presence of existing params in URL
                const divider = this.api.speakersRequest.indexOf('?') > -1 ? '&' : '?';

                // Return computed string
                return `${this.api.speakersRequest}${divider}${params}`;
            },
            displayedStates () { 
                return this.data.states.filter(e => e.slug !== 'national');
            }
        },
        watch: {
            /**
             * Watch computed property
             * @param {string} url URL containing query parameters
             * @param {string} prevVal Previous value
             */
            speakersQueryParams: function (url, prevVal) { // eslint-disable-line
                // Set loading state to true
                this.loading = true;

                // Fire new AJAX request
                this.getSpeakers(url)
                    .then((speakers) => {
                        // Update property with new speakers
                        this.data.speakers = speakers;

                        // Sort
                        this.sortSpeakers();

                        // If previous value is not null, this is not the initial load
                        if (prevVal) {
                            // Update loading state
                            this.loading = false;
                        }
                    });
            },
        },
        mounted() {
            const speakerIdQuery = (new URLSearchParams(window.location.search)).get('speakerId');
            const speakerTitleQuery = (new URLSearchParams(window.location.search)).get('speaker');

            if (speakerIdQuery) {
                this.$refs.search.searchValue = speakerTitleQuery;
                this.api.search.speakerId = speakerIdQuery;
            }

            // Set speaker state request URL
            this.api.speakersRequest = '/api/speakers.json';
            // Set speaker expertise request URL
            this.api.expertiseRequest = '/api/expertise.json';
            // Set speaker states request URL
            this.api.statesRequest = '/api/states.json';
            // Init requests
            axios.all([
                this.getFilterCategories(this.api.expertiseRequest),
            ]).then(axios.spread((expertise) => {
                // Update propertes
                this.data.expertise = expertise;

                // Update loading state
                this.loading = false;
            }));

            axios.all([
                this.getFilterCategories(this.api.statesRequest),
            ]).then(axios.spread((states) => {
                this.data.states = states;
                
                this.loading = false;
            }));
            EventBus.$on('speaker-modal', this.openModal);
            EventBus.$on('modal-close', this.closeModal)
        },
        destroyed() {
            EventBus.$off('speaker-modal', this.openModal);
            EventBus.$off('modal-close', this.closeModal)
        },
    
        methods: {
            /**
             * Get speaker data
             * @param {string} requestURL The API endpoint
             */
            getSpeakers(requestURL) {
                return axios.get(requestURL)
                    // Get data
                    .then((response) => {
                        // Set property
                        this.data.speakersMeta = response.data.meta;
                        // Return data to next then
                        return response.data.data;
                    });

            },

            /**
             * Get data to use in Filter
             * @param {string} requestURL The API endpoint
             */
            getFilterCategories(requestURL) {
                return axios.get(requestURL)
                    // Get data
                    .then(response => response.data.data);
            },
            handleSearch({ key, values }) {
                this.view.activeModelSearchTerm = values.model || null;
                this.updateUrlParams({ key, values });
            },
            handlePageChange(pageNumber) {
                this.updateUrlParams({ key: 'page', values: [pageNumber] });
            },
            /**
             * Update URL parameters on main request
             * Emitted by: CheckboxFilters, Pagination, Search
             * @param {string} key Property of this.api object to update
             * @param {array} values Items to add to array
             */
            updateUrlParams({ key, values }) {
                // Is values an array?
                const isArray = Array.isArray(values);

                if (isArray) {
                    // Empty target array
                    this.api[key] = [];

                    // Add values to target array
                    values.forEach(value => this.api[key].push(value));
                } else {
                    // Update key with string
                    this.api[key] = values;
                }
                if (key !== 'page') this.api.page = 1;
            },

            sortSpeakers() {
                // If no speakers, stop
                if (!this.data.speakers) return false;

                // Sort alphabetically
                return this.data.speakers.sort((a, b) => { // eslint-disable-line arrow-body-style
                    return a.title < b.title ? -1 : 1;
                });
            },

            /**
             * Toggle view filters boolean
             */
            toggleViewFilters() {
                this.view.filters = !this.view.filters;
            },

            openModal() {
                this.$refs.modal.open();
            },

            closeModal() {
                this.$refs.modal.close();
            }
        },
    });
})();
